import React, { useState, ReactNode, MouseEvent, FocusEvent, useRef, useEffect } from 'react';
import { useUser } from '@/app/hooks/useUser'; // Assuming you have a useUser hook for user state
import BaseModal from '@/app/base-ui/BaseModalUI';
import { useRouter, usePathname } from 'next/navigation';
import { useGlobalDialog } from '@/app/contexts/global-dialog';
import utilsService from '@/app/helpers/utilsService';
import { useTranslation } from '@/app/hooks/useTranslation';

interface PermissionWrapperProps {
    children: ReactNode;
}

const PermissionWrapper: React.FC<PermissionWrapperProps> = ({ children }) => {
    const { auth } = useUser();
    const [modalOpen, setModalOpen] = useState(false);
    const {loadingDialog} = useGlobalDialog()
    const blockActionsRef = useRef(false);
    const router = useRouter();
    const pathname = usePathname();
    const { t } = useTranslation();
    const checkPermissions = (): boolean => {
        const hasPermission = auth?.isRegistered ?? false;
        if (!hasPermission) {
            setModalOpen(true);
            blockActionsRef.current = true;
        }
        return hasPermission;
    };

    const handleInteraction = (e: MouseEvent | FocusEvent) => {
        if (blockActionsRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        if (!checkPermissions()) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        blockActionsRef.current = true; // Block further actions
    };

    const handleAccept = () => {
        // Redirect to login
        loadingDialog.open()
        router.replace(utilsService.buildLoginUrl(pathname));
    };

    useEffect(() => {
        if (!modalOpen) {
            blockActionsRef.current = false; // Reset blocking when modal is closed
        }
    }, [modalOpen]);

    return (
        <>
            <div
                onClick={handleInteraction}
                onMouseDown={handleInteraction}
                onFocus={handleInteraction}
                style={{ pointerEvents: 'auto' }}
            >
                {children}
            </div>
            {modalOpen && (
                <BaseModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    title={t('login_required', '로그인이 필요한 기능입니다.')}
                    description={t('go_to_login_confirm', '로그인 페이지로 이동하시겠습니까?')}
                    showCancel
                    showAccept
                    onCancel={handleCloseModal}
                    onAccept={handleAccept}
                    cancelText={t('cancel', '취소')}
                    acceptText={t('login', '로그인')}
                />
            )}
        </>
    );
};

export default PermissionWrapper;
